import { useState, type FormEvent } from 'react'
import { Navigate, useNavigate, useParams } from 'react-router-dom'
import {
  formatMoney,
  itemOfferSummary,
  routeStores,
  type CatalogItem,
  type Offer,
  type Store,
  type Template,
} from '@sp/shared'
import { useSignedIn } from '../session.tsx'
import { api } from '../lib/api.ts'
import { HttpError } from '../lib/http.ts'
import { useCatalog, useFx, useHistory, useOfferMatches, useStores, useTemplates, useTrips } from '../lib/groupData.ts'
import { useDesktop } from '../lib/useDesktop.ts'
import { handleListPaste } from '../lib/paste.ts'
import {
  formatQty,
  itemsCount,
  relativeAgo,
  searchKey,
  splitEntries,
  storeShortName,
  storeSub,
  type ParsedEntry,
} from '../lib/format.ts'
import { matchesByItem } from '../lib/offers.ts'
import { errorMessage } from '../lib/useResource.ts'
import { Icon } from '../components/Icon.tsx'
import { CopyTemplateSheet } from '../components/CopyTemplateSheet.tsx'
import {
  AddTemplateToTripSheet,
  NewTemplateSheet,
  TemplateList,
  TemplateSettingsSheet,
  WatchRow,
} from '../components/TemplateSheets.tsx'
import {
  Banner,
  Button,
  Card,
  Chip,
  EmptyState,
  IconButton,
  PageTitle,
  Paragraph,
  SectionHead,
  SelectField,
  SelectRow,
  Spinner,
  Stepper,
  TextCol,
  TextField,
} from '../components/ui.tsx'
import { DeskToolbar, Inspector, Panel } from '../components/desk.tsx'
import { DealTag, templateDealLabel } from '../components/OfferBadge.tsx'
import { TemplateDealsSheet } from '../components/TemplateDealsSheet.tsx'
import { inDealText } from '../components/DealsSheet.tsx'
import { CatalogItemForm, CatalogItemSheet } from '../components/CatalogItemSheet.tsx'
import { OfferSheet } from '../components/OfferSheet.tsx'
import { useContextMenu, type ContextAction } from '../components/ContextMenu.tsx'
import { useConfirm } from '../components/ConfirmSheet.tsx'

function itemCurrency(item: CatalogItem, stores: readonly Store[]) {
  return item.typicalCurrency ?? stores.find((s) => s.id === item.storeId)?.currency ?? null
}

function itemPrice(item: CatalogItem, stores: readonly Store[]): string | null {
  const currency = itemCurrency(item, stores)
  return item.typicalPriceMinor !== null && currency ? formatMoney(item.typicalPriceMinor, currency) : null
}

/** Zpatky do tvaru, ktery splitEntries znovu precte stejne ("2 kg Rýže"). */
function entryText(entry: ParsedEntry): string {
  if (entry.qty === 1 && !entry.unit) return entry.name
  const qty = String(entry.qty).replace('.', ',')
  return `${qty} ${entry.unit ? `${entry.unit} ` : ''}${entry.name}`
}

/**
 * Jedna sablona skupiny (navrh v2). Pravidelny nakup se chova jako drivejsi
 * jedina sablona; ostatni sablony maji vlastni mnozstvi zbozi, zbozi se do nich
 * pridava i z toho, co skupina uz zna, a odebrani ho nemaze.
 */
export function TemplatePage() {
  const { templateId = '' } = useParams<{ templateId: string }>()
  const s = useSignedIn()
  const groupId = s.group.id
  const desktop = useDesktop()
  const navigate = useNavigate()
  const templates = useTemplates(groupId)
  const catalog = useCatalog(groupId)
  const stores = useStores(groupId, true)
  const history = useHistory(groupId)
  const matches = useOfferMatches(groupId, null, templateId ? `template:${templateId}` : null)
  const [editing, setEditing] = useState<CatalogItem | null>(null)
  const [pickedOffer, setPickedOffer] = useState<Offer | null>(null)
  const trips = useTrips(groupId)
  const [text, setText] = useState('')
  const [storeChoice, setStoreChoice] = useState<string | null>(null)
  const [query, setQuery] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [copying, setCopying] = useState(false)
  const [settings, setSettings] = useState(false)
  const [toTrip, setToTrip] = useState(false)
  const [creating, setCreating] = useState(false)
  const [reviewing, setReviewing] = useState(false)
  const { onContextMenu, contextMenu } = useContextMenu()
  const { confirm, confirmSheet } = useConfirm()

  const list = templates.res.state === 'ok' ? templates.res.data : []
  const template = list.find((t) => t.id === templateId) ?? null
  const allStores = stores.res.state === 'ok' ? stores.res.data : []
  const route = routeStores(allStores)
  const addStoreId = storeChoice ?? route[0]?.id ?? ''
  const catalogItems = catalog.res.state === 'ok' ? catalog.res.data : []
  const members = new Map((template?.items ?? []).map((i) => [i.catalogItemId, i.qty]))
  const items = catalogItems.filter((ci) => members.has(ci.id))
  const historyByItem = new Map((history.res.state === 'ok' ? history.res.data : []).map((h) => [h.catalogItemId, h]))
  const offersByItem = matchesByItem(matches.res.state === 'ok' ? matches.res.data : [])
  const fx = useFx()
  const rates = fx.res.state === 'ok' ? fx.res.data : { eurCzk: null, plnCzk: null }
  // Obchod, ktery se nenacetl (nebo zmizel), bereme jako obchod bez letaku — radsi „jinde" nez cizi cena.
  const offerSummary = (ci: CatalogItem) =>
    itemOfferSummary(
      offersByItem.get(ci.id),
      ci.storeId ? (allStores.find((st) => st.id === ci.storeId) ?? { offerChain: null }) : null,
      rates,
    )

  if (templates.res.state === 'ok' && !template) return <Navigate to="/sablony" replace />
  if (!template) {
    return templates.res.state === 'error' ? <Paragraph tone="error">{templates.res.message}</Paragraph> : <Spinner />
  }

  const regular = template.regular
  const failed = (err: unknown) =>
    setError(err instanceof HttpError && err.isOffline ? 'Šablonu jde měnit jen se signálem.' : errorMessage(err))
  const replaceTemplate = (next: Template) => templates.setData((prev) => prev.map((t) => (t.id === next.id ? next : t)))
  const qtyOf = (ci: CatalogItem) => members.get(ci.id) ?? ci.defaultQty

  async function add(e: FormEvent) {
    e.preventDefault()
    const entries = splitEntries(text)
    if (entries.length === 0 || !template) return
    setBusy(true)
    setError(null)
    // Kazda polozka se propise hned. Kdyz treti z peti selze, v poli zustanou
    // jen neulozene — opakovane odeslani uz prvni dve nezdvoji.
    const remaining = [...entries]
    try {
      for (const entry of entries) {
        if (regular) {
          const created = await api.catalog.create(groupId, {
            name: entry.name,
            defaultQty: entry.qty,
            unit: entry.unit,
            storeId: addStoreId || null,
          })
          // Server u zname zbozi vrati existujici polozku — v cache se nesmi zdvojit.
          catalog.setData((prev) => (prev.some((i) => i.id === created.id) ? prev : [...prev, created]))
          templates.setData((prev) =>
            prev.map((t) =>
              t.id === template.id && !t.items.some((i) => i.catalogItemId === created.id)
                ? { ...t, items: [...t.items, { catalogItemId: created.id, qty: null }] }
                : t,
            ),
          )
        } else {
          const result = await api.templates.addItem(template.id, {
            name: entry.name,
            qty: entry.qty,
            unit: entry.unit,
            storeId: addStoreId || null,
          })
          if (result.created) catalog.setData((prev) => [...prev, result.item])
          replaceTemplate(result.template)
        }
        remaining.shift()
      }
      setText('')
    } catch (err) {
      setText(remaining.map(entryText).join(', '))
      failed(err)
    } finally {
      setBusy(false)
    }
  }

  async function addKnown(ci: CatalogItem) {
    if (!template) return
    setBusy(true)
    setError(null)
    try {
      replaceTemplate((await api.templates.addItem(template.id, { name: ci.name })).template)
      setText('')
    } catch (err) {
      failed(err)
    } finally {
      setBusy(false)
    }
  }

  async function setQty(ci: CatalogItem, qty: number) {
    if (!template || !(qty > 0)) return
    const value = qty === ci.defaultQty ? null : qty
    replaceTemplate({ ...template, items: template.items.map((i) => (i.catalogItemId === ci.id ? { ...i, qty: value } : i)) })
    try {
      replaceTemplate(await api.templates.setItemQty(template.id, ci.id, value))
    } catch (err) {
      failed(err)
      templates.reload()
    }
  }

  async function removeFromTemplate(ci: CatalogItem) {
    if (!template) return
    replaceTemplate({ ...template, items: template.items.filter((i) => i.catalogItemId !== ci.id) })
    try {
      replaceTemplate(await api.templates.removeItem(template.id, ci.id))
    } catch (err) {
      failed(err)
      templates.reload()
    }
  }

  async function toggleWatch() {
    if (!template) return
    const watchOffers = !template.watchOffers
    replaceTemplate({ ...template, watchOffers })
    try {
      replaceTemplate(await api.templates.update(template.id, { watchOffers }))
    } catch (err) {
      failed(err)
      replaceTemplate({ ...template, watchOffers: !watchOffers })
    }
  }

  const needle = searchKey(query)
  const visible = needle ? items.filter((i) => searchKey(i.name).includes(needle)) : items
  const byName = (a: CatalogItem, b: CatalogItem) => a.name.localeCompare(b.name, 'cs')
  const sections = [
    ...route.map((store) => ({ key: store.id, title: store.name, items: visible.filter((i) => i.storeId === store.id) })),
    {
      key: 'none',
      title: 'Bez obchodu',
      items: visible.filter((i) => !i.storeId || !route.some((store) => store.id === i.storeId)),
    },
  ]
    .filter((section) => section.items.length > 0)
    .map((section) => ({ ...section, items: [...section.items].sort(byName) }))

  // Do vyberu obchodu patri i archivovany obchod, na ktery upravovana polozka ukazuje.
  const editingStore = editing?.storeId ? allStores.find((store) => store.id === editing.storeId) : undefined
  const storeOptions = editingStore && editingStore.archived ? [...route, editingStore] : route

  const saved = (item: CatalogItem) => {
    const before = catalogItems.find((i) => i.id === item.id)
    catalog.setData((prev) =>
      prev.some((i) => i.id === item.id) ? prev.map((i) => (i.id === item.id ? item : i)) : [...prev, item],
    )
    // Jine hledane vyrazy (nebo nazev, podle ktereho se bez nich hleda) = jine akce.
    if (before && (before.offerTerms !== item.offerTerms || before.name !== item.name)) matches.reload()
  }
  const itemOffers = (ci: CatalogItem) => ({
    list: offersByItem.get(ci.id) ?? [],
    rates,
    thumbs: !desktop,
    onPick: setPickedOffer,
  })
  const archived = (id: string) => {
    catalog.setData((prev) => prev.filter((i) => i.id !== id))
    templates.setData((prev) => prev.map((t) => ({ ...t, items: t.items.filter((i) => i.catalogItemId !== id) })))
  }

  /** Prave tlacitko na sablone v seznamu (pocitac). */
  const templateMenu = (t: Template) =>
    onContextMenu((): ContextAction[] => {
      const actions: ContextAction[] = [
        { label: 'Otevřít', icon: 'chevron_right', onSelect: () => navigate(`/sablony/${t.id}`) },
      ]
      if (!t.regular) {
        actions.push({
          label: 'Smazat šablonu',
          icon: 'trash',
          tone: 'error',
          onSelect: () =>
            void (async () => {
              const ok = await confirm({
                title: `Smazat šablonu „${t.name}“?`,
                text: 'Zboží zůstane ve skupině i v ostatních šablonách.',
                confirmLabel: 'Smazat šablonu',
                danger: true,
              })
              if (!ok) return
              try {
                await api.templates.archive(t.id)
                templates.setData((prev) => prev.filter((x) => x.id !== t.id))
                if (t.id === template?.id) navigate('/sablony', { replace: true })
              } catch (err) {
                failed(err)
              }
            })(),
        })
      }
      return actions
    }, t.name)

  /** Prave tlacitko na polozce v tabulce (pocitac). */
  const itemMenu = (ci: CatalogItem) =>
    onContextMenu((): ContextAction[] => {
      const actions: ContextAction[] = [{ label: 'Upravit', icon: 'pencil', onSelect: () => setEditing(ci) }]
      if (!regular) {
        actions.push({ label: 'O kus víc', icon: 'plus', onSelect: () => void setQty(ci, qtyOf(ci) + 1) })
      }
      actions.push(
        {
          label: regular ? 'Odebrat z Pravidelného' : 'Odebrat ze šablony',
          icon: 'minus',
          onSelect: () => {
            void removeFromTemplate(ci)
            if (editing?.id === ci.id) setEditing(null)
          },
        },
        {
          label: 'Smazat úplně',
          icon: 'trash',
          tone: 'error',
          onSelect: () =>
            void (async () => {
              const ok = await confirm({
                title: `Smazat „${ci.name}“?`,
                text: 'Zmizí ze všech šablon skupiny. Staré nákupy zůstanou beze změny.',
                confirmLabel: 'Smazat ze všech šablon',
                danger: true,
              })
              if (!ok) return
              try {
                await api.catalog.archive(ci.id)
                archived(ci.id)
                if (editing?.id === ci.id) setEditing(null)
              } catch (err) {
                failed(err)
              }
            })(),
        },
      )
      return actions
    }, ci.name)

  // Slevy v sablone: stejny pruh a kontrola jako v nakupu.
  const dealEntries = items.flatMap((ci) => {
    const offers = offersByItem.get(ci.id)
    return offers ? [{ item: ci, offers }] : []
  })
  const dealsBanner = dealEntries.length > 0 && (
    <Banner
      tone="info"
      className="banner-deal deals-strip"
      action={
        <Button compact onClick={() => setReviewing(true)}>
          Projít
        </Button>
      }
    >
      <span className="deals-strip-icon" aria-hidden="true">
        %
      </span>
      <TextCol title={inDealText(dealEntries.length, true)} sub="v obchodech skupiny" />
    </Banner>
  )

  // Znamé zbozi skupiny, ktere v sablone jeste neni — nabidne se pri psani.
  const addNeedle = searchKey(text)
  const suggestions =
    !regular && addNeedle.length >= 2 && !text.includes(',')
      ? catalogItems.filter((ci) => !members.has(ci.id) && searchKey(ci.name).includes(addNeedle)).slice(0, 5)
      : []

  const subtitle = regular
    ? 'Věci, které kupujete pravidelně. Do nákupu z nich jen vybereš.'
    : `${itemsCount(items.length)} · do nákupu je přidáš najednou`

  const addForm = (
    <>
      <form className="add-row" onSubmit={(e) => void add(e)}>
        <TextField
          aria-label="Přidat do šablony"
          placeholder={regular ? 'Přidat do šablony' : 'Přidat zboží – napiš název'}
          value={text}
          onChange={(e) => setText(e.target.value)}
          onPaste={(e) => handleListPaste(e, setText)}
          className="grow"
          enterKeyHint="done"
          maxLength={500}
          autoComplete="off"
        />
        {route.length > 0 && (
          <SelectField
            ariaLabel="Obchod pro nové položky"
            value={addStoreId}
            onChange={setStoreChoice}
            className="add-store"
            options={[
              ...route.map((store) => ({
                value: store.id,
                label: store.name,
                sub: storeSub(store),
                triggerLabel: storeShortName(store.name),
              })),
              { value: '', label: 'Bez obchodu' },
            ]}
          />
        )}
        <IconButton icon="plus" label="Přidat do šablony" bordered type="submit" disabled={busy} />
      </form>
      {suggestions.length > 0 && (
        <div className="tpl-suggest">
          <span className="meta muted">Skupina už zná:</span>
          {suggestions.map((ci) => (
            <Chip key={ci.id} plus disabled={busy} onClick={() => void addKnown(ci)}>
              {ci.name}
            </Chip>
          ))}
        </div>
      )}
      <Paragraph size="meta">
        {regular
          ? `Víc věcí najednou odděl čárkou. Cenu a obvyklé množství doplníš ${desktop ? 'kliknutím' : 'ťuknutím'} na položku.`
          : 'Víc věcí odděl čárkou. Zboží, které skupina už zná, si vezme obchod, cenu i hledání v akcích.'}
      </Paragraph>
      {error && <Paragraph tone="error">{error}</Paragraph>}
    </>
  )

  const states = (
    <>
      {catalog.res.state === 'loading' && <Spinner />}
      {catalog.res.state === 'error' && <Paragraph tone="error">{catalog.res.message}</Paragraph>}
      {catalog.res.state === 'ok' && items.length === 0 && (
        <EmptyState title="Šablona je prázdná">
          {regular
            ? 'Přidej věci, které kupujete pravidelně – máslo, mléko, kávu. Při plánování je pak jen odškrtneš.'
            : 'Přidej zboží, které se na tuhle příležitost kupuje – třeba maso na gril, bulky a papírové tácky.'}
        </EmptyState>
      )}
      {needle && sections.length === 0 && items.length > 0 && <Paragraph>Nic takového v šabloně není.</Paragraph>}
    </>
  )

  const canCopy = s.groups.length > 1 && items.length > 0
  const sheets = (
    <>
      <TemplateSettingsSheet
        open={settings}
        onClose={() => setSettings(false)}
        template={template}
        canCopy={canCopy}
        onSaved={replaceTemplate}
        onDeleted={() => {
          templates.setData((prev) => prev.filter((t) => t.id !== template.id))
          navigate('/sablony', { replace: true })
        }}
        onCopy={() => {
          setSettings(false)
          setCopying(true)
        }}
      />
      <AddTemplateToTripSheet open={toTrip} onClose={() => setToTrip(false)} groupId={groupId} template={template} />
      <NewTemplateSheet
        open={creating}
        onClose={() => setCreating(false)}
        groupId={groupId}
        onCreated={(created) => {
          templates.setData((prev) => [...prev, created])
          navigate(`/sablony/${created.id}`)
        }}
      />
      <CopyTemplateSheet
        open={copying}
        onClose={() => setCopying(false)}
        groupId={groupId}
        items={items}
        stores={allStores}
        fromTemplateId={regular ? undefined : template.id}
      />
      {reviewing && (
        <TemplateDealsSheet
          groupId={groupId}
          entries={dealEntries}
          stores={allStores}
          trips={trips.res.state === 'ok' ? trips.res.data : []}
          rates={rates}
          onClose={() => setReviewing(false)}
        />
      )}
      {contextMenu}
      {confirmSheet}
      <OfferSheet
        offer={pickedOffer}
        catalogItem={editing}
        onClose={() => setPickedOffer(null)}
        addTo={{ trips: trips.res.state === 'ok' ? trips.res.data : [], stores: allStores }}
      />
    </>
  )

  const search = (className?: string) => (
    <TextField
      type="search"
      aria-label="Hledat v šabloně"
      placeholder="Hledat v šabloně"
      value={query}
      onChange={(e) => setQuery(e.target.value)}
      className={className}
    />
  )

  const itemSub = (ci: CatalogItem) =>
    [regular ? formatQty(ci.defaultQty, ci.unit) : null, itemPrice(ci, allStores), ci.category].filter(Boolean).join(' · ')

  if (desktop) {
    return (
      <div className="desk-view tpl-view">
        <TemplateList templates={list} onNew={() => setCreating(true)} onMenu={templateMenu} />
        <div className="desk-view-main">
          <DeskToolbar
            crumb="Šablony"
            title={template.name}
            sub={subtitle}
            actions={
              <>
                {search('desk-search')}
                <IconButton icon="more" label="Nastavení šablony" bordered onClick={() => setSettings(true)} />
                <Button kind="primary" icon="basket" onClick={() => setToTrip(true)}>
                  Nakoupit
                </Button>
              </>
            }
          />
          <Panel>
            <div className="stack-8">
              <WatchRow template={template} onToggle={() => void toggleWatch()} />
              {addForm}
            </div>
          </Panel>
          {dealsBanner}
          {states}
          {sections.length > 0 && (
            <Panel className="tpl-table">
              <div className="tpl-grid tpl-head" aria-hidden="true">
                <span>Název</span>
                <span className="is-right">{regular ? 'Množství' : 'V šabloně'}</span>
                <span className="is-right">Cena</span>
                <span>Kategorie</span>
                <span>Naposledy</span>
              </div>
              {sections.map((section) => (
                <section key={section.key} aria-label={section.title}>
                  <div className="tpl-section">
                    <h2 className="panel-title">{section.title}</h2>
                    <span className="panel-meta">{itemsCount(section.items.length)}</span>
                  </div>
                  {section.items.map((item) => {
                    const price = itemPrice(item, allStores)
                    const summary = offerSummary(item)
                    const selected = editing?.id === item.id
                    return (
                      <button
                        key={item.id}
                        type="button"
                        className={`tpl-row tpl-grid${selected ? ' is-selected' : ''}`}
                        aria-pressed={selected}
                        onClick={() => setEditing(item)}
                        onContextMenu={itemMenu(item)}
                      >
                        <span className="tpl-name">
                          <span className="tpl-name-text">{item.name}</span>
                          {summary && <DealTag label={templateDealLabel(summary)} />}
                        </span>
                        <span className="tpl-cell is-right">{formatQty(qtyOf(item), item.unit)}</span>
                        <span className={`tpl-cell is-right${price ? '' : ' is-muted'}`}>{price ?? '—'}</span>
                        <span className="tpl-cell is-muted">{item.category ?? '—'}</span>
                        <span className="tpl-cell is-muted">
                          {item.lastBoughtAt ? relativeAgo(item.lastBoughtAt) : 'zatím nekoupeno'}
                        </span>
                      </button>
                    )
                  })}
                </section>
              ))}
            </Panel>
          )}
        </div>
        {editing && (
          <Inspector label="Upravit položku">
            <div className="inspector-head">
              <div className="stack-4">
                <h2 className="inspector-title">Upravit položku</h2>
                <span className="meta muted">{editing.name}</span>
              </div>
              <IconButton icon="x" label="Zavřít úpravy" tone="muted" bordered onClick={() => setEditing(null)} />
            </div>
            <CatalogItemForm
              key={editing.id}
              history={historyByItem.get(editing.id)}
              groupId={groupId}
              stores={storeOptions}
              item={editing}
              defaultStoreId={addStoreId}
              offers={itemOffers(editing)}
              onSaved={(item) => {
                saved(item)
                setEditing(item)
              }}
              onArchived={(id) => {
                archived(id)
                setEditing(null)
              }}
              onRemoveFromTemplate={() => {
                void removeFromTemplate(editing)
                setEditing(null)
              }}
              templateName={template.name}
              templateQty={regular ? undefined : { value: qtyOf(editing), onChange: (qty) => void setQty(editing, qty) }}
              onDone={() => undefined}
            />
          </Inspector>
        )}
        {sheets}
      </div>
    )
  }

  return (
    <>
      <div className="tpl-back">
        <Button kind="text" compact icon="chevron_left" onClick={() => navigate('/sablony')}>
          Šablony
        </Button>
      </div>
      <PageTitle
        title={template.name}
        subtitle={subtitle}
        action={<IconButton icon="more" label="Nastavení šablony" onClick={() => setSettings(true)} />}
      />
      <WatchRow template={template} onToggle={() => void toggleWatch()} />
      {dealsBanner}

      <Card gap="sm">{addForm}</Card>

      {items.length > 8 && search()}
      {states}

      {sections.map((section) => (
        <section key={section.key} className="stack-8">
          <SectionHead title={section.title} meta={itemsCount(section.items.length)} />
          {section.items.map((ci) => {
            const summary = offerSummary(ci)
            const sub = itemSub(ci)
            const subNode = summary ? (
              <span className="offer-sub">
                {sub && <span>{sub}</span>}
                <DealTag label={templateDealLabel(summary)} />
              </span>
            ) : (
              sub || undefined
            )
            if (regular) {
              return (
                <SelectRow key={ci.id} as="button" pad="even" onClick={() => setEditing(ci)}>
                  <TextCol title={ci.name} sub={subNode} />
                  <Icon name="chevron_right" size={20} className="tone-muted" />
                </SelectRow>
              )
            }
            const qty = qtyOf(ci)
            return (
              <SelectRow key={ci.id} className="tpl-item-row">
                <button type="button" className="row-main" onClick={() => setEditing(ci)}>
                  <TextCol title={ci.name} sub={subNode} />
                </button>
                <Stepper
                  value={formatQty(qty, ci.unit)}
                  decrementLabel={`Méně: ${ci.name}`}
                  incrementLabel={`Více: ${ci.name}`}
                  onDecrement={() => void setQty(ci, qty - 1)}
                  onIncrement={() => void setQty(ci, qty + 1)}
                />
              </SelectRow>
            )
          })}
        </section>
      ))}

      {/* Hlavni akce stranky vpravo dole nad listou. */}
      <Button kind="primary" icon="basket" className="fab" onClick={() => setToTrip(true)}>
        Nakoupit
      </Button>

      <CatalogItemSheet
        open={editing !== null}
        history={editing ? historyByItem.get(editing.id) : undefined}
        onClose={() => setEditing(null)}
        groupId={groupId}
        stores={storeOptions}
        item={editing}
        defaultStoreId={addStoreId}
        offers={editing ? itemOffers(editing) : undefined}
        onSaved={saved}
        onArchived={archived}
        onRemoveFromTemplate={
          editing
            ? () => {
                void removeFromTemplate(editing)
                setEditing(null)
              }
            : undefined
        }
        templateName={template.name}
        templateQty={
          editing && !regular ? { value: qtyOf(editing), onChange: (qty) => void setQty(editing, qty) } : undefined
        }
      />
      {sheets}
    </>
  )
}
